import { Alert, Box, Chip, CircularProgress, Container, Grid, Link, Paper, Stack, Tab, Table, TableBody, TableCell,
  TableHead, TableRow, Tabs, Tooltip, Typography } from "@mui/material";
import { useEffect } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Info, Kpi } from "@/components/VulnerabilityWatch/Info.tsx";
import PortalTab from "@/components/VulnerabilityWatch/Portal/PortalTab.tsx";
import SourcesTab from "@/components/VulnerabilityWatch/Sources/SourcesTab.tsx";
import WindowCaption from "@/components/VulnerabilityWatch/WindowCaption.tsx";
import { isWatchKnown, usePortalOverview, useVulnerabilityWatch } from "@/hooks/useVulnerabilityWatch.ts";
import { useVulnerabilityWatchSeen } from "@/hooks/useVulnerabilityWatchSeen.ts";
import { useWatchTab, WATCH_TABS, type WatchTab } from "@/hooks/useWatchTab.ts";
import { formatIsoDateTime } from "@/utils/formatDateTime.ts";
import { knownOverview, windowCves } from "@/utils/portalOverview.ts";
import * as copy from "@/utils/vulnerabilityCopy.ts";
import type { WatchStatus } from "@/utils/vulnerabilityCopy.ts";
import type { PortalCve, VulnerabilityWatch, WatchVulnerability } from "@/types";

const STATUS_COLOR: Record<WatchStatus, "error" | "warning" | "success"> = { affects: "error", check: "warning", fixed: "success" };

export function StatusChip({ status, reason }: { readonly status: WatchStatus; readonly reason?: string }) {
  const chip = <Chip size="small" color={STATUS_COLOR[status]} label={copy.statusLabel(status)} tabIndex={reason ? 0 : undefined} />;
  return reason ? <Tooltip title={reason}>{chip}</Tooltip> : chip;
}

function Overview({ data }: { readonly data: VulnerabilityWatch }) {
  const product = data.products.find((p) => p.product) ?? data.products[0] ?? null;
  const vulns = data.vulnerabilities;
  const known = isWatchKnown(data);
  const count = (status: WatchStatus) => (known ? vulns.filter((v) => v.status === status).length.toLocaleString("en-US") : copy.NOT_KNOWN);
  const productLabel = product ? copy.productLabel(product.vendor, product.product, product.version) : copy.NOT_KNOWN;
  const reportedBy = productLabel === copy.NOT_KNOWN ? copy.reportedByLabel(data.products) : null;
  return (
    <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 3 }}>
          <Kpi label={copy.KPI_PRODUCT} tip={copy.TIP_PRODUCT} value={productLabel} />
          {reportedBy && <Typography variant="caption" color="text.secondary">{reportedBy}</Typography>}
        </Grid>
        <Grid size={{ xs: 4, md: 3 }}><Kpi label={copy.KPI_LISTED} tip={copy.TIP_LISTED} value={known ? vulns.length.toLocaleString("en-US") : copy.NOT_KNOWN} /></Grid>
        <Grid size={{ xs: 4, md: 3 }}><Kpi label={copy.KPI_AFFECTS} tip={copy.TIP_AFFECTS} value={count("affects")} /></Grid>
        <Grid size={{ xs: 4, md: 3 }}><Kpi label={copy.KPI_CHECK} tip={copy.TIP_CHECK} value={count("check")} /></Grid>
      </Grid>
    </Paper>
  );
}

function VulnerabilityTable({ vulnerabilities, known, cves }: {
  readonly vulnerabilities: readonly WatchVulnerability[];
  readonly known: boolean;
  readonly cves: ReadonlyMap<string, PortalCve> | null;
}) {
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>{copy.COL_VULNERABILITY}</TableCell>
          <TableCell>{copy.COL_LISTED}</TableCell>
          <TableCell>{copy.COL_STATUS}</TableCell>
          <TableCell>{copy.COL_DETECTION}<Info text={copy.TIP_DETECTION} /></TableCell>
          <TableCell>{copy.COL_AT_PORTAL}<Info text={copy.TIP_AT_PORTAL} /></TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {!known && (
          <TableRow>
            <TableCell colSpan={5}>{copy.NOT_KNOWN}</TableCell>
          </TableRow>
        )}
        {known && vulnerabilities.map((v) => (
          <TableRow key={v.cve}>
            <TableCell>
              <Link component={RouterLink} to={`/vulnerability-watch/${encodeURIComponent(v.cve)}`} sx={{ fontWeight: 600 }}>{v.name}</Link>
              <Typography variant="caption" sx={{ display: "block", fontFamily: "monospace" }}>{v.cve}</Typography>
            </TableCell>
            <TableCell>{v.listed_on}</TableCell>
            <TableCell><StatusChip status={v.status} reason={v.reason} /></TableCell>
            <TableCell>
              {copy.detectionLabel(v.detection)}
              {v.detection === "none" && <Info text={copy.TIP_NOT_VISIBLE} />}
            </TableCell>
            <TableCell>{copy.atPortalLabel(v.detection, cves !== null, cves?.get(v.cve))}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function VulnerabilitiesTab() {
  const { data, isLoading, error } = useVulnerabilityWatch();
  const overview = usePortalOverview();
  const { markSeen } = useVulnerabilityWatchSeen();
  useEffect(() => {
    if (data && isWatchKnown(data)) markSeen(data.vulnerabilities.map((v) => v.cve));
  }, [data, markSeen]);
  if (isLoading) return <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}><CircularProgress /></Box>;
  if (error || !data) return <Alert severity="error" sx={{ mt: 2 }}>{copy.LOAD_FAILED}</Alert>;

  const known = isWatchKnown(data);
  const cves = windowCves(knownOverview(overview.data));
  return (
    <>
      <Overview data={data} />
      <Paper variant="outlined" sx={{ p: 3 }}>
        {known && data.vulnerabilities.length === 0 ? (
          <Typography color="text.secondary">{copy.NO_VULNERABILITIES}</Typography>
        ) : (
          <VulnerabilityTable vulnerabilities={data.vulnerabilities} known={known} cves={cves} />
        )}
        <Stack direction="row" sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary">
            {copy.CREDIT}
            {data.kev?.fetched_at && ` ${copy.listFetchedLabel(formatIsoDateTime(data.kev.fetched_at))}`}
          </Typography>
        </Stack>
      </Paper>
    </>
  );
}

const TAB_LABELS: Record<WatchTab, string> = {
  vulnerabilities: copy.TAB_VULNERABILITIES,
  portal: copy.AT_PORTAL_TITLE,
  sources: copy.TAB_SOURCES,
};

export default function VulnerabilityWatchPage() {
  const [tab, selectTab] = useWatchTab();
  const overview = usePortalOverview();
  return (
    <Container maxWidth={false} sx={{ py: 4, px: { xs: 2, sm: 3, md: 4 } }}>
      <Typography variant="h6" sx={{ fontWeight: 600 }}>{copy.TITLE}</Typography>
      <WindowCaption overview={overview.data} />
      <Tabs value={tab} onChange={(_event, next: WatchTab) => selectTab(next)} aria-label={copy.TABS_LABEL}
        variant="scrollable" allowScrollButtonsMobile sx={{ mb: 3, borderBottom: 1, borderColor: "divider" }}>
        {WATCH_TABS.map((value) => (
          <Tab key={value} value={value} label={TAB_LABELS[value]} id={`watch-tab-${value}`} aria-controls={`watch-panel-${value}`} />
        ))}
      </Tabs>
      <Box role="tabpanel" id={`watch-panel-${tab}`} aria-labelledby={`watch-tab-${tab}`}>
        {tab === "vulnerabilities" && <VulnerabilitiesTab />}
        {tab === "portal" && <PortalTab />}
        {tab === "sources" && <SourcesTab />}
      </Box>
    </Container>
  );
}
